import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Checkbox,
  InputPrefix,
  InputSuffix,
  TextBox,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";

import { Box, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPCountBadge from "../../../shared/components/shared/CPCountBadge/CPCountBadge";
import http from "shared/http";
import "./AdminTiersPageStyles.scss";
import useAppState from "shared/state/useAppState";

// Types
// Rename this interface (and the TierData fields) when copying this page for a different domain.
interface ItemRecord {
  id: number;
  code: string;
  name: string;
  company: string;
  isActive: boolean;
}

interface TierData {
  unassigned: ItemRecord[];
  bronze: ItemRecord[];
  silver: ItemRecord[];
  gold: ItemRecord[];
}

type TierKey = "bronze" | "silver" | "gold";
type TierCode = "BRONZE" | "SILVER" | "GOLD";

interface TierConfig {
  key: TierKey;
  code: TierCode;
  label: string;
  color: string;
  lightBg: string;
}

const TIERS: TierConfig[] = [
  {
    key: "bronze",
    code: "BRONZE",
    label: "Bronze",
    color: "#b5651d",
    lightBg: "#ececec",
  },
  {
    key: "silver",
    code: "SILVER",
    label: "Silver",
    color: "#546e7a",
    lightBg: "#ececec",
  },
  {
    key: "gold",
    code: "GOLD",
    label: "Gold",
    color: "#b8860b",
    lightBg: "#ececec",
  },
];

// API hooks
const TIERS_QUERY_KEY = ["agency-tiers"];

const useAgencyTiersQuery = () =>
  useQuery<TierData>(
    TIERS_QUERY_KEY,
    async () => {
      const res = await http.fetchAsync("admin/tiers");
      return {
        unassigned: res.unassigned ?? [],
        bronze: res.bronze ?? [],
        silver: res.silver ?? [],
        gold: res.gold ?? [],
      };
    },
    { staleTime: 60 * 1000 },
  );

const useUpdateTierMutation = () =>
  useMutation(
    async (payload: { agencyIds: number[]; tier: TierCode | null }) => {
      await http.putAsync("admin/agencies/tier", payload);
    },
  );

// Status badge
const ActiveBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span
    className={`agency-active-badge agency-active-badge--${isActive ? "active" : "inactive"}`}
    title={isActive ? "Active" : "Inactive"}
  >
    {isActive ? "A" : "I"}
  </span>
);

// Helpers
const filterAgencies = (
  agencies: ItemRecord[],
  filter: string,
): ItemRecord[] => {
  if (!filter.trim()) return agencies;
  const lower = filter.toLowerCase();
  return agencies.filter(
    (a) =>
      a.name.toLowerCase().includes(lower) ||
      a.code.toLowerCase().includes(lower) ||
      a.company.toLowerCase().includes(lower),
  );
};

// Unassigned Panel
interface UnassignedPanelProps {
  agencies: ItemRecord[];
  onAssign: (ids: number[], tier: TierCode) => void;
}

const UnassignedPanel: React.FC<UnassignedPanelProps> = ({
  agencies,
  onAssign,
}) => {
  const [filter, setFilter] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());

  const filtered = React.useMemo(
    () => filterAgencies(agencies, filter),
    [agencies, filter],
  );

  // Drop stale selections when agencies list changes after a mutation
  React.useEffect(() => {
    const validIds = new Set(agencies.map((a) => a.id));
    setSelectedIds((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [agencies]);

  const filteredSelectedCount = filtered.filter((a) =>
    selectedIds.has(a.id),
  ).length;
  const allFilteredSelected =
    filtered.length > 0 && filteredSelectedCount === filtered.length;
  const someFilteredSelected =
    filteredSelectedCount > 0 && !allFilteredSelected;

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((a) => next.delete(a.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((a) => next.add(a.id));
        return next;
      });
    }
  };

  const toggleItem = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAssign = (tier: TierCode) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    onAssign(ids, tier);
    setSelectedIds(new Set());
  };

  const selectedCount = selectedIds.size;

  return (
    <Box className="tiers-left-panel" flexDirection="column">
      {/* Header */}
      <Box
        className="tiers-panel-header tiers-unassigned-header"
        flexDirection="row"
        alignItems="center"
        spacing={5}
      >
        <CPText bold>Unassigned</CPText>
        <CPCountBadge className="tiers-count-badge">
          {agencies.length}
        </CPCountBadge>
      </Box>

      {/* Filter */}
      <Box className="tiers-filter-row">
        <TextBox
          className="tiers-filter-textbox"
          placeholder="Filter by code, name, or company..."
          value={filter}
          onChange={(e: TextBoxChangeEvent) => setFilter(e.value as string)}
          prefix={() => (
            <InputPrefix>
              <i className="fa-solid fa-magnifying-glass tiers-filter-icon" />
            </InputPrefix>
          )}
          suffix={
            filter
              ? () => (
                  <InputSuffix>
                    <Button
                      fillMode="flat"
                      size="small"
                      onClick={() => setFilter("")}
                      title="Clear filter"
                    >
                      <i className="fa-solid fa-xmark" />
                    </Button>
                  </InputSuffix>
                )
              : undefined
          }
        />
      </Box>

      {/* Select-all */}
      <Box className="tiers-select-all-row">
        <Checkbox
          id="tiers-select-all"
          checked={allFilteredSelected}
          ref={(handle) => {
            const el = handle?.element?.querySelector("input");
            if (el) el.indeterminate = someFilteredSelected;
          }}
          onChange={handleSelectAll}
          disabled={filtered.length === 0}
          label={
            filter
              ? `Select visible (${filtered.length})`
              : `Select all (${filtered.length})`
          }
        />
      </Box>

      {/* Agency list */}
      <Box className="tiers-agency-list" flexDirection="column">
        {filtered.map((agency) => (
          <Box
            key={agency.id}
            className={`tiers-agency-item${selectedIds.has(agency.id) ? " tiers-agency-item--selected" : ""}`}
            flexDirection="row"
            alignItems="center"
            onClick={() => toggleItem(agency.id)}
          >
            <Checkbox
              checked={selectedIds.has(agency.id)}
              onChange={() => toggleItem(agency.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <CPText className="tiers-agency-code">{agency.code}</CPText>
            <CPText className="tiers-agency-name" title={agency.name}>
              {agency.name}
            </CPText>
            <ActiveBadge isActive={agency.isActive} />
          </Box>
        ))}
        {filtered.length === 0 && (
          <CPText className="tiers-empty-state">
            {filter
              ? "No agencies match the filter."
              : "All agencies have been assigned to a tier."}
          </CPText>
        )}
      </Box>

      {/* Sticky action bar – visible only when items are selected */}
      <Box
        className={`tiers-action-bar${selectedCount > 0 ? " tiers-action-bar--visible" : ""}`}
        flexDirection="row"
        alignItems="center"
      >
        <CPText style={{ color: "#475569" }}>Assign {selectedCount}</CPText>
        <Box flexDirection="row" spacing={6} className="tiers-assign-buttons">
          {TIERS.map((t) => (
            <Button
              key={t.key}
              fillMode="outline"
              size="small"
              className={`tiers-assign-btn tiers-assign-btn--${t.key}`}
              onClick={() => handleAssign(t.code)}
            >
              <span
                className="tier-dot-sm"
                style={{ backgroundColor: t.color }}
              />
              {t.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Tier Panel
interface TierPanelProps {
  tier: TierConfig;
  agencies: ItemRecord[];
  onReassign: (agencyId: number, toTier: TierCode | null) => void;
  onBulkReassign: (ids: number[], toTier: TierCode | null) => void;
}

const TierPanel: React.FC<TierPanelProps> = ({
  tier,
  agencies,
  onReassign,
  onBulkReassign,
}) => {
  const [filter, setFilter] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());

  const filtered = React.useMemo(
    () => filterAgencies(agencies, filter),
    [agencies, filter],
  );

  // Drop stale selections when agencies list changes after a mutation
  React.useEffect(() => {
    const validIds = new Set(agencies.map((a) => a.id));
    setSelectedIds((prev) => {
      const next = new Set<number>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [agencies]);

  const filteredSelectedCount = filtered.filter((a) =>
    selectedIds.has(a.id),
  ).length;
  const allFilteredSelected =
    filtered.length > 0 && filteredSelectedCount === filtered.length;
  const someFilteredSelected =
    filteredSelectedCount > 0 && !allFilteredSelected;
  const selectedCount = selectedIds.size;

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((a) => next.delete(a.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((a) => next.add(a.id));
        return next;
      });
    }
  };

  const toggleItem = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkAction = (toTier: TierCode | null) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    onBulkReassign(ids, toTier);
    setSelectedIds(new Set());
  };

  const otherTiers = TIERS.filter((t) => t.key !== tier.key);

  return (
    <Box
      className="tier-panel"
      flexDirection="column"
      style={
        {
          "--tier-color": tier.color,
          "--tier-bg": tier.lightBg,
        } as React.CSSProperties
      }
    >
      {/* Header */}
      <Box
        className="tier-panel-header"
        flexDirection="row"
        alignItems="center"
      >
        <Box
          className="tier-panel-title"
          flexDirection="row"
          alignItems="center"
          spacing={8}
          style={{ padding: 0 }}
        >
          <Checkbox
            checked={allFilteredSelected}
            ref={(handle) => {
              const el = handle?.element?.querySelector("input");
              if (el) el.indeterminate = someFilteredSelected;
            }}
            onChange={handleSelectAll}
            disabled={filtered.length === 0}
            title="Select all visible"
          />
          <span className="tier-dot" style={{ backgroundColor: tier.color }} />
          <CPText bold style={{ color: tier.color }}>
            {tier.label}
          </CPText>
          <CPCountBadge
            className="tiers-count-badge"
            style={{ backgroundColor: tier.color, color: "#fff" }}
          >
            {agencies.length}
          </CPCountBadge>
          {selectedCount > 0 && (
            <CPText className="tier-selected-label">
              ({selectedCount} selected)
            </CPText>
          )}
        </Box>
        <Box className="tier-panel-right">
          {selectedCount > 0 ? (
            <Box
              flexDirection="row"
              spacing={4}
              alignItems="center"
              className="tier-bulk-actions"
            >
              {otherTiers.map((t) => (
                <Button
                  key={t.key}
                  fillMode="outline"
                  size="small"
                  className={`tier-move-btn tier-move-btn--${t.key}`}
                  title={`Move selected to ${t.label}`}
                  onClick={() => handleBulkAction(t.code)}
                >
                  <span
                    className="tier-dot-sm"
                    style={{ backgroundColor: t.color }}
                  />
                  {t.label}
                </Button>
              ))}
              <Button
                fillMode="flat"
                size="small"
                className="tier-remove-btn"
                title="Unassign selected"
                onClick={() => handleBulkAction(null)}
              >
                <i className="fa-solid fa-trash-can" />
              </Button>
            </Box>
          ) : (
            <Box className="tier-panel-filter">
              <TextBox
                className="tiers-filter-textbox tiers-filter-textbox--compact"
                placeholder="Filter…"
                value={filter}
                onChange={(e: TextBoxChangeEvent) =>
                  setFilter(e.value as string)
                }
                prefix={() => (
                  <InputPrefix>
                    <i className="fa-solid fa-magnifying-glass tiers-filter-icon" />
                  </InputPrefix>
                )}
                suffix={
                  filter
                    ? () => (
                        <InputSuffix>
                          <Button
                            fillMode="flat"
                            size="small"
                            onClick={() => setFilter("")}
                            title="Clear"
                          >
                            <i className="fa-solid fa-xmark" />
                          </Button>
                        </InputSuffix>
                      )
                    : undefined
                }
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Item list */}
      <Box className="tier-panel-content" flexDirection="column">
        {filtered.length === 0 ? (
          <CPText className="tiers-empty-state">
            {filter
              ? "No agencies match the filter."
              : `No agencies assigned to ${tier.label}.`}
          </CPText>
        ) : (
          <table className="tier-agency-table">
            <tbody>
              {filtered.map((agency) => (
                <tr
                  key={agency.id}
                  className={`tier-agency-row${selectedIds.has(agency.id) ? " tier-agency-row--selected" : ""}`}
                  onClick={() => toggleItem(agency.id)}
                >
                  <td className="tier-td tier-td--checkbox">
                    <Checkbox
                      checked={selectedIds.has(agency.id)}
                      onChange={() => toggleItem(agency.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="tier-td tier-td--code">
                    <span className="tier-td-code-inner">
                      <ActiveBadge isActive={agency.isActive} />
                      {agency.code}
                    </span>
                  </td>
                  <td className="tier-td tier-td--name">{agency.name}</td>
                  <td className="tier-td tier-td--company">{agency.company}</td>
                  <td className="tier-td tier-td--actions">
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      spacing={4}
                      className="tier-row-actions"
                    >
                      {otherTiers.map((t) => (
                        <Button
                          key={t.key}
                          fillMode="outline"
                          size="small"
                          className={`tier-move-btn tier-move-btn--${t.key}`}
                          title={`Move to ${t.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onReassign(agency.id, t.code);
                          }}
                        >
                          <span
                            className="tier-dot-sm"
                            style={{ backgroundColor: t.color }}
                          />
                          {t.label}
                        </Button>
                      ))}
                      <Button
                        fillMode="flat"
                        size="small"
                        className="tier-remove-btn"
                        title="Unassign (move back to unassigned)"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReassign(agency.id, null);
                        }}
                      >
                        <i className="fa-solid fa-trash-can" />
                      </Button>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
    </Box>
  );
};

// Page
const AdminTiersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg, setIsLoading } = useAppState();
  const { data, isLoading, isError } = useAgencyTiersQuery();
  const mutation = useUpdateTierMutation();

  const [localTiers, setLocalTiers] = React.useState<TierData>({
    unassigned: [],
    bronze: [],
    silver: [],
    gold: [],
  });

  // Sync server data into local state (initial load + refetch)
  React.useEffect(() => {
    if (data) setLocalTiers(data);
  }, [data]);

  React.useEffect(() => {
    setIsLoading(isLoading || mutation.isLoading);
  }, [isLoading, mutation.isLoading, setIsLoading]);

  const handleAssignFromUnassigned = React.useCallback(
    (ids: number[], tierCode: TierCode) => {
      const tierKey = tierCode.toLowerCase() as TierKey;
      const idSet = new Set(ids);

      setLocalTiers((prev) => {
        const moved = prev.unassigned.filter((a) => idSet.has(a.id));
        return {
          ...prev,
          unassigned: prev.unassigned.filter((a) => !idSet.has(a.id)),
          [tierKey]: [...prev[tierKey], ...moved].sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        };
      });

      mutation.mutate(
        { agencyIds: ids, tier: tierCode },
        { onError: () => queryClient.invalidateQueries(TIERS_QUERY_KEY) },
      );
    },
    [mutation, queryClient],
  );

  const handleReassign = React.useCallback(
    (agencyId: number, fromTierKey: TierKey, toTierCode: TierCode | null) => {
      const toTierKey = toTierCode
        ? (toTierCode.toLowerCase() as TierKey)
        : null;

      setLocalTiers((prev) => {
        const agency = prev[fromTierKey].find((a) => a.id === agencyId);
        if (!agency) return prev;

        const updated: TierData = {
          ...prev,
          [fromTierKey]: prev[fromTierKey].filter((a) => a.id !== agencyId),
        };

        if (toTierKey) {
          updated[toTierKey] = [...prev[toTierKey], agency].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
        } else {
          updated.unassigned = [...prev.unassigned, agency].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
        }

        return updated;
      });

      mutation.mutate(
        { agencyIds: [agencyId], tier: toTierCode },
        { onError: () => queryClient.invalidateQueries(TIERS_QUERY_KEY) },
      );
    },
    [mutation, queryClient],
  );

  const handleBulkReassign = React.useCallback(
    (ids: number[], fromTierKey: TierKey, toTierCode: TierCode | null) => {
      const toTierKey = toTierCode
        ? (toTierCode.toLowerCase() as TierKey)
        : null;
      const idSet = new Set(ids);

      setLocalTiers((prev) => {
        const moved = prev[fromTierKey].filter((a) => idSet.has(a.id));
        const updated: TierData = {
          ...prev,
          [fromTierKey]: prev[fromTierKey].filter((a) => !idSet.has(a.id)),
        };

        if (toTierKey) {
          updated[toTierKey] = [...prev[toTierKey], ...moved].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
        } else {
          updated.unassigned = [...prev.unassigned, ...moved].sort((a, b) =>
            a.name.localeCompare(b.name),
          );
        }

        return updated;
      });

      mutation.mutate(
        { agencyIds: ids, tier: toTierCode },
        { onError: () => queryClient.invalidateQueries(TIERS_QUERY_KEY) },
      );
    },
    [mutation, queryClient],
  );

  return (
    <Box className="admin-tiers-page" flexDirection="column">
      {/* Page header */}
      <Box className="tiers-page-header">
        <Box
          flexDirection="row"
          alignItems="center"
          spacing={6}
          justifyContent={"flex-start"}
        >
          <CPText>Agency Tier Management</CPText>
        </Box>
      </Box>

      {/* Mutation error banner */}
      {mutation.isError && (
        <Box
          className="tiers-error-banner"
          flexDirection="row"
          alignItems="center"
        >
          <CPFontAwesome inline icon="fa-solid fa-triangle-exclamation" />
          <CPText fontSize="0.8rem">
            Changes could not be saved — data has been refreshed to reflect the
            current state.
          </CPText>
        </Box>
      )}

      {/* Main layout */}
      <Box
        flexDirection="row"
        className="tiers-main-layout"
        style={{ padding: 0 }}
      >
        <UnassignedPanel
          agencies={localTiers.unassigned}
          onAssign={handleAssignFromUnassigned}
        />
        <Box
          flexDirection="column"
          className="tiers-right-area"
          style={{ padding: 0 }}
        >
          {TIERS.map((tier) => (
            <TierPanel
              key={tier.key}
              tier={tier}
              agencies={localTiers[tier.key]}
              onReassign={(agencyId, toTierCode) =>
                handleReassign(agencyId, tier.key, toTierCode)
              }
              onBulkReassign={(ids, toTierCode) =>
                handleBulkReassign(ids, tier.key, toTierCode)
              }
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminTiersPage;
export { AdminTiersPage };
